import { ThemedText } from "@/components/themed-text";
import { VehicleStatusRow } from "@/components/ui/vehicle-status-box";
import { BrandColors } from "@/constants/theme";
import { useAuth } from "@/contexts/auth-context";
import { Booking } from "@/lib/services/bookings";
import { getRemoteImageSource } from "@/lib/utils/media-url";
import Ionicons from "@react-native-vector-icons/ionicons";
import { Image } from "expo-image";
import { useMemo, useState } from "react";
import { StyleSheet, View } from "react-native";

interface VehicleInformationProps {
  booking: Booking;
  variant?: "list" | "detail";
}

const getFuelIcon = (fuelType?: string): keyof typeof Ionicons.glyphMap => {
  const fuel = fuelType?.toLowerCase() ?? "";
  if (fuel.includes("electric")) return "flash-outline";
  if (
    fuel.includes("diesel") ||
    fuel.includes("petrol") ||
    fuel.includes("gas")
  ) {
    return "water-outline";
  }
  return "water-outline";
};

export const VehicleInformation: React.FC<VehicleInformationProps> = ({
  booking,
  variant = "detail",
}) => {
  const { theme } = useAuth();
  const [imageError, setImageError] = useState(false);
  const imageSource = useMemo(
    () => getRemoteImageSource(booking?.vehicle_image_path),
    [booking?.vehicle_image_path],
  );
  const showVehicleImage = imageSource !== null && !imageError;
  const vehicleTitle = [booking.vehicle_make, booking.vehicle_model]
    .filter(Boolean)
    .join(" ");

  const detailPills = [
    booking.vehicle_body_type && {
      icon: "car-outline" as const,
      label: booking.vehicle_body_type,
    },
    booking.vehicle_weight && {
      icon: "calendar-outline" as const,
      label: booking.vehicle_weight ? `${booking.vehicle_weight} kg` : "",
    },
    booking.vehicle_fuel_type && {
      icon: "water-outline" as const,
      label: booking.vehicle_fuel_type,
    },
  ].filter(Boolean) as {
    icon: keyof typeof Ionicons.glyphMap;
    label: string;
  }[];

  return (
    <View style={styles.section}>
      {variant === "detail" && (
        <View style={styles.sectionHeader}>
          <Ionicons name="car-outline" size={18} color={BrandColors.primary} />
          <ThemedText style={styles.sectionTitle} type="defaultSemiBold">
            Vehicle information
          </ThemedText>
        </View>
      )}

      <View
        style={[
          styles.card,
          {
            backgroundColor: theme.card,
            borderColor: theme.border,
            shadowColor: theme.background,
          },
        ]}
      >
        {variant === "list" && (
          <View style={styles.sectionHeader}>
            <ThemedText type="extraSmallSemiBold" style={{ fontSize: 14 }}>
              Vehicle information
            </ThemedText>
          </View>
        )}
        <View style={styles.cardContent}>
          {showVehicleImage ? (
            <Image
              source={imageSource}
              style={styles.vehicleImage}
              contentFit="contain"
              transition={200}
              recyclingKey={imageSource?.uri}
              onError={() => setImageError(true)}
            />
          ) : (
            <View style={[styles.vehicleImage, styles.noImageContainer]}>
              <Ionicons
                name="car-outline"
                size={32}
                color={BrandColors.mediumGray}
              />
            </View>
          )}

          <View style={styles.vehicleDetails}>
            <ThemedText
              style={styles.vehicleTitle}
              type="defaultSemiBold"
              numberOfLines={2}
            >
              {vehicleTitle || booking.vehicle_registration || "Vehicle"}
            </ThemedText>

            <View style={styles.metaRow}>
              {booking.vehicle_colour ? (
                <View style={styles.metaItem}>
                  <View style={styles.colorDot} />
                  <ThemedText style={styles.metaText} type="extraSmall">
                    {booking.vehicle_colour}
                  </ThemedText>
                </View>
              ) : null}
              {booking.vehicle_fuel_type ? (
                <View style={styles.metaItem}>
                  <Ionicons
                    name={getFuelIcon(booking.vehicle_fuel_type)}
                    size={12}
                    color={BrandColors.mediumGray}
                  />
                  <ThemedText style={styles.metaText} type="extraSmall">
                    {booking.vehicle_fuel_type}
                  </ThemedText>
                </View>
              ) : null}
              {booking.vehicle_year ? (
                <View style={styles.metaItem}>
                  <Ionicons
                    name="bag-outline"
                    size={12}
                    color={BrandColors.mediumGray}
                  />
                  <ThemedText style={styles.metaText} type="extraSmall">
                    {booking.vehicle_year}
                  </ThemedText>
                </View>
              ) : null}
            </View>

            {detailPills.length > 0 ? (
              <View style={styles.pillRow}>
                {detailPills.map((pill) => (
                  <View key={pill.label} style={styles.pill}>
                    <Ionicons
                      name={pill.icon}
                      size={12}
                      color={BrandColors.primary}
                    />
                    <ThemedText style={styles.pillText} type="extraSmall">
                      {pill.label}
                    </ThemedText>
                  </View>
                ))}
              </View>
            ) : null}
          </View>
        </View>

        <VehicleStatusRow
          motStatus={booking.vehicle_mot_status}
          motExpiryDate={booking.vehicle_expiry_date}
          taxStatus={booking.vehicle_tax_status}
          taxDueDate={booking.vehicle_tax_due_date}
          style={styles.statusRow}
        />
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  section: {
    paddingHorizontal: 16,
    marginBottom: 8,
  },
  sectionHeader: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    marginBottom: 12,
  },
  sectionTitle: {
    fontSize: 16,
    fontWeight: "700",
  },
  card: {
    borderRadius: 16,
    padding: 14,
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.05,
    shadowRadius: 8,
    elevation: 2,
    borderWidth: 1,
    gap: 12,
  },
  cardContent: {
    flexDirection: "row",
    gap: 14,
  },
  vehicleImage: {
    width: 88,
    height: 88,
    borderRadius: 12,
    backgroundColor: BrandColors.backgroundGray,
  },
  noImageContainer: {
    alignItems: "center",
    justifyContent: "center",
  },
  vehicleDetails: {
    flex: 1,
    gap: 8,
  },
  vehicleTitle: {
    fontSize: 15,
    fontWeight: "700",
    lineHeight: 20,
    textTransform: "uppercase",
  },
  metaRow: {
    flexDirection: "row",
    flexWrap: "wrap",
    alignItems: "center",
    gap: 10,
  },
  metaItem: {
    flexDirection: "row",
    alignItems: "center",
    gap: 4,
  },
  colorDot: {
    width: 8,
    height: 8,
    borderRadius: 4,
    backgroundColor: BrandColors.mediumGray,
  },
  metaText: {
    color: BrandColors.mediumGray,
    textTransform: "capitalize",
  },
  pillRow: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 6,
  },
  pill: {
    flexDirection: "row",
    alignItems: "center",
    gap: 4,
    backgroundColor: BrandColors.primaryLight,
    borderRadius: 20,
    paddingHorizontal: 10,
    paddingVertical: 5,
  },
  pillText: {
    color: BrandColors.primary,
    fontWeight: "600",
    textTransform: "capitalize",
  },
  statusRow: {
    marginTop: 4,
  },
});
